import { useLazyQuery } from '@apollo/client/react';
import { differenceInMinutes } from 'date-fns';
import { useEffect, useState } from 'react';

import {
  GetDispensingRequestOnDraftAppointmentDocument,
  type PfDispensingRequestFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import {
  formatMedicalInstitution,
  GET_DISPENSING_REQUEST_INTERVAL_MINUTES,
} from '~/utils/pf_dispensing_request';

const ONE_MINUTE = 60 * 1000;

export const useFetchDispensingRequest = (draftAppointmentId: string | null) => {
  const [error, setError] = useState<Error | null>(null);
  const [loadedDate, setLoadedDate] = useState<Date | null>(null);
  const [abortedTimerId, setAbortedTimerId] = useState<number | null>(null);
  const [isTimeout, setIsTimeout] = useState(false);
  const [controller] = useState(new AbortController());

  const [getDispensingRequest, { called, loading, data, error: dispensingRequestError }] =
    useLazyQuery(GetDispensingRequestOnDraftAppointmentDocument, {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    });
  const draftAppointment = getNode(data, 'DraftAppointment');
  const dispensingRequest = draftAppointment?.pfDispensingRequest;
  const medicalInstitution = dispensingRequest
    ? formatMedicalInstitution(dispensingRequest as PfDispensingRequestFragment)
    : null;

  const draftAppointmentStatus = draftAppointment?.status;
  const previousDraftAppointmentStatus = draftAppointment?.previousStatus;
  const willReceivePrescription = draftAppointmentStatus === 'available';
  const hasReceivedPrescription =
    draftAppointmentStatus === 'finished' ||
    draftAppointmentStatus === 'wait_for_booking' ||
    (draftAppointmentStatus === 'cancelled' &&
      previousDraftAppointmentStatus === 'wait_for_booking');

  useEffect(() => {
    if (!data) return;
    setLoadedDate(new Date());

    if (abortedTimerId !== null) {
      clearTimeout(abortedTimerId);
    }
  }, [abortedTimerId, data]);

  useEffect(() => {
    if (!dispensingRequestError) return;
    setError(dispensingRequestError);
  }, [dispensingRequestError]);

  useEffect(() => {
    if (draftAppointmentId) {
      setError(null);

      getDispensingRequest({
        variables: { draftAppointmentId },
        context: { fetchOptions: { signal: controller.signal } },
      });
    }
  }, [getDispensingRequest, draftAppointmentId, error, controller.signal]);

  useEffect(() => {
    if (draftAppointmentId) {
      setError(null);

      // ref. pharmacy/components/partials/DispensingRequestCollapse/use-fetch-dispensing_requests.ts
      if (
        !loadedDate ||
        differenceInMinutes(new Date(), loadedDate) > GET_DISPENSING_REQUEST_INTERVAL_MINUTES
      ) {
        getDispensingRequest({
          variables: { draftAppointmentId },
          context: { fetchOptions: { signal: controller.signal } },
        });
      }
    }
  }, [getDispensingRequest, loadedDate, draftAppointmentId, controller.signal]);

  useEffect(() => {
    const _timerId = window.setTimeout(() => {
      controller.abort();
      setIsTimeout(true);
    }, ONE_MINUTE);
    setAbortedTimerId(_timerId);

    return () => {
      if (abortedTimerId !== null) {
        clearTimeout(abortedTimerId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading: !called || loading,
    timeout: isTimeout,
    error,
    willReceivePrescription,
    hasReceivedPrescription,
    dispensingRequest,
    medicalInstitution,
  };
};
